import { useEffect, useState } from "react";
import { useToast, type Toast, type ToastType } from "@/contexts/toast-context";
import { CheckCircle, XCircle, AlertCircle, Info, X } from "lucide-react";

const getToastStyles = (type: ToastType) => {
  switch (type) {
    case "success":
      return {
        icon: CheckCircle,
        iconColor: "text-green-600",
        progressColor: "bg-green-600",
        borderColor: "border-border",
      };
    case "error":
      return {
        icon: XCircle,
        iconColor: "text-red-600",
        progressColor: "bg-red-600",
        borderColor: "border-border",
      };
    case "warning":
      return {
        icon: AlertCircle,
        iconColor: "text-orange-600",
        progressColor: "bg-orange-600",
        borderColor: "border-border",
      };
    default:
      return {
        icon: Info,
        iconColor: "text-blue-600",
        progressColor: "bg-blue-600",
        borderColor: "border-border",
      };
  }
};

function ToastItem({ toast }: { toast: Toast }) {
  const { hideToast } = useToast();
  const [progress, setProgress] = useState(0);
  const styles = getToastStyles(toast.type);
  const Icon = styles.icon;

  useEffect(() => {
    const duration = toast.duration || 2000;
    const interval = 50; // Update every 50ms for smooth animation
    const steps = duration / interval;
    let currentStep = 0;

    const timer = setInterval(() => {
      currentStep++;
      const newProgress = (currentStep / steps) * 100;
      setProgress(newProgress);

      if (currentStep >= steps) {
        clearInterval(timer);
      }
    }, interval);

    return () => clearInterval(timer);
  }, [toast.duration]);

  return (
    <div
      className={`relative bg-background border ${styles.borderColor} rounded-lg shadow-lg p-4 mb-3 min-w-80 max-w-md overflow-hidden animate-in slide-in-from-right-full duration-300`}
    >
      {/* Close button */}
      <button
        onClick={() => hideToast(toast.id)}
        className="absolute top-2 right-2 text-muted-foreground hover:text-foreground transition-colors"
      >
        <X className="w-4 h-4" />
      </button>

      {/* Content */}
      <div className="flex items-start gap-3 pr-6">
        <Icon className={`w-5 h-5 flex-shrink-0 mt-0.5 ${styles.iconColor}`} />
        <div className="flex-1 min-w-0">
          {toast.title && (
            <h4 className="text-sm font-medium text-foreground mb-1">
              {toast.title}
            </h4>
          )}
          <p className="text-xs text-muted-foreground">{toast.message}</p>
        </div>
      </div>

      {/* Progress bar */}
      <div className="absolute bottom-0 left-0 right-0 h-1 bg-muted">
        <div
          className={`h-full ${styles.progressColor} transition-all duration-50 ease-linear`}
          style={{ width: `${progress}%` }}
        />
      </div>
    </div>
  );
}

export function ToastContainer() {
  const { toasts } = useToast();

  if (toasts.length === 0) return null;

  return (
    <div className="fixed bottom-4 right-4 z-50 space-y-3">
      {toasts.map((toast) => (
        <ToastItem key={toast.id} toast={toast} />
      ))}
    </div>
  );
}
